import React, { useState, useEffect, useRef } from 'react';
import brogrammers_log from '@/assets/images/bro.jpg';
import { SignInButton, SignUpButton } from '@clerk/clerk-react';
import { useLocation } from 'react-router-dom';
// PERFORMANCE: Tree-shake icon imports to reduce bundle size
import { Twitter } from 'lucide-react';
import { Instagram } from 'lucide-react';
import { Linkedin } from 'lucide-react';
import { Menu } from 'lucide-react';
import { X } from 'lucide-react';
import { Layers } from 'lucide-react';
import { Target } from 'lucide-react';
import { Bot } from 'lucide-react';
import { FileText } from 'lucide-react';
import { UploadCloud } from 'lucide-react';
import { BrainCircuit } from 'lucide-react';
import { TrendingUp } from 'lucide-react';
import { ChevronDown } from 'lucide-react';
import { ArrowUp } from 'lucide-react';
import { BookOpen } from 'lucide-react';

// --- Data Constants ---
const FEATURES_DATA = [
  {
    icon: Layers,
    title: 'AI Flashcards',
    description: 'Generate dynamic flashcards from your notes to master key concepts faster.',
  },
  {
    icon: Target,
    title: 'Interactive Quizzes',
    description: 'Test your knowledge with adaptive quizzes that focus on your weak spots.',
  },
  {
    icon: Bot,
    title: '24/7 AI Chat Tutor',
    description: 'Get instant, step-by-step answers to any question, anytime.',
  },
  {
    icon: FileText,
    title: 'Smart Note Summaries',
    description: 'Automatically condense long documents and lectures into concise, readable notes.',
  },
];

const HOW_IT_WORKS_STEPS = [
  {
    icon: UploadCloud,
    title: '1. Upload Your Materials',
    description: 'Simply upload your course documents, lecture notes, or even a YouTube link.',
  },
  {
    icon: BrainCircuit,
    title: '2. Learn with AI Tools',
    description: 'Our AI analyzes your content and generates flashcards, quizzes, and summaries.',
  },
  {
    icon: TrendingUp,
    title: '3. Track & Improve',
    description: 'Monitor your progress with detailed analytics and watch your scores soar.',
  },
];

const TESTIMONIALS_DATA = [
  {
    name: 'Priya Singh',
    role: 'Medical Student',
    quote:
      "ZenshKool's AI flashcards are a lifesaver for anatomy. I can finally visualize and remember complex terms. My grades have improved by over 20%!",
    avatar: 'P',
  },
  {
    name: 'Rahul Verma',
    role: 'Engineering Aspirant',
    quote:
      'The AI chatbot is like having a personal tutor for physics. It explains difficult concepts clearly, and the quizzes help me practice for my entrance exams.',
    avatar: 'R',
  },
  {
    name: 'Aisha Khan',
    role: 'UPSC Candidate',
    quote:
      'Condensing entire history books into summaries has been crucial for my revision. ZenshKool saves me hundreds of hours. Truly a revolutionary tool.',
    avatar: 'A',
  },
  {
    name: 'Suresh Kumar',
    role: 'Law Student',
    quote:
      'The smart summaries feature is incredible for case law. It boils down lengthy judgments into key points, which has been invaluable for my exam prep.',
    avatar: 'S',
  },
];

const FAQ_DATA = [
  {
    question: 'What subjects does ZenshKool support?',
    answer:
      "ZenshKool is designed to work with any text-based subject. It's particularly effective for content-heavy fields like medicine, law, history, and competitive exams, but can be used for anything from literature to physics.",
  },
  {
    question: 'Is my data secure?',
    answer:
      'Absolutely. We use end-to-end encryption to protect your data. Your uploaded materials are private and are only used to power your personal AI learning tools.',
  },
  {
    question: 'Can I use ZenshKool on multiple devices?',
    answer:
      'Yes, your ZenshKool account is synced across all your devices. You can start a study session on your laptop and continue on your phone or tablet seamlessly.',
  },
  {
    question: 'Do you offer plans for institutions?',
    answer:
      'Yes, we offer custom plans for schools, colleges, and coaching centers. Please contact our sales team for a demo and tailored pricing.',
  },
];

// --- Helper Hooks & Components ---

const useIntersectionObserver = (options) => {
  const [entry, setEntry] = useState(null);
  const [node, setNode] = useState(null);
  const observer = useRef(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new window.IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setEntry(entry);
        observer.current.unobserve(entry.target);
      }
    }, options);
    const { current: currentObserver } = observer;
    if (node) currentObserver.observe(node);
    return () => currentObserver.disconnect();
  }, [node, options]);

  return [setNode, entry];
};

const Section = ({ children, className, ...props }) => {
  const [ref, entry] = useIntersectionObserver({ threshold: 0.1 });
  const isVisible = !!entry;
  return (
    <section
      ref={ref}
      className={`transition-all duration-1000 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      } ${className || ''}`}
      {...props}>
      {children}
    </section>
  );
};

// --- SVG Doodles Component ---
const Doodle = ({ type, className }) => {
  const doodles = {
    plane: (
      <svg viewBox="0 0 50 50" className={className}>
        <path
          d="M2.1,22.9C2.1,22.9,2.1,22.9,2.1,22.9l19.9,2.5c0,0,0,0,0,0l2.5,19.9c0,0,0,0,0,0L2.1,22.9z M47.9,2.1L2.1,22.9 c0,0,0,0,0,0l19.9,2.5c0,0,0,0,0,0l2.5,19.9c0,0,0,0,0,0L47.9,2.1z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    atom: (
      <svg viewBox="0 0 50 50" className={className}>
        <circle cx="25" cy="25" r="3" fill="currentColor" />
        <ellipse cx="25" cy="25" rx="20" ry="8" fill="none" stroke="currentColor" strokeWidth="1" />
        <ellipse
          cx="25"
          cy="25"
          rx="20"
          ry="8"
          transform="rotate(60 25 25)"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
        />
        <ellipse
          cx="25"
          cy="25"
          rx="20"
          ry="8"
          transform="rotate(-60 25 25)"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
        />
      </svg>
    ),
    formula: (
      <svg viewBox="0 0 100 40" className={className}>
        <text x="0" y="30" fontFamily="Space Mono, monospace" fontSize="20" fill="currentColor">
          E=mc²
        </text>
      </svg>
    ),
    swirl: (
      <svg viewBox="0 0 50 50" className={className}>
        <path
          d="M25,2.5 C38.8,2.5 47.5,11.2 47.5,25 C47.5,38.8 38.8,47.5 25,47.5 C11.2,47.5 2.5,38.8 2.5,25 C2.5,16.8 7.3,9.7 14.1,5.8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1"
          strokeLinecap="round"
        />
      </svg>
    ),
    plus: (
      <svg viewBox="0 0 50 50" className={className}>
        <line x1="25" y1="10" x2="25" y2="40" stroke="currentColor" strokeWidth="1" />
        <line x1="10" y1="25" x2="40" y2="25" stroke="currentColor" strokeWidth="1" />
      </svg>
    ),
  };
  return doodles[type] || null;
};

// --- Main Page Components ---

const Header = ({ onMenuToggle, isMenuOpen }) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const originalRequestUrl = location.pathname + location.search;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const linkColorClass = 'text-[var(--text-light)] hover:text-[var(--brand-accent)]';
  const brandColorClass = 'text-white';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-slate-900/70 backdrop-blur-lg shadow-lg border-b border-slate-700/50' : 'bg-transparent'
      }`}>
      <div className="container mx-auto px-6 py-4">
        <nav className="flex justify-between items-center">
          <a
            href="#home"
            className={`flex items-center gap-2 text-3xl font-bold font-mono transition-colors ${brandColorClass}`}>
            <img src={brogrammers_log} alt="logo" className="size-8 rounded-lg" />
            Brogrammers
          </a>
          <div className="hidden md:flex space-x-6 items-center">
            <a href="#features" className={`transition-colors ${linkColorClass}`}>
              Features
            </a>
            <a href="#how-it-works" className={`transition-colors ${linkColorClass}`}>
              How It Works
            </a>
            <a href="#faq" className={`transition-colors ${linkColorClass}`}>
              FAQ
            </a>

            <SignInButton
              mode="modal"
              fallbackRedirectUrl={originalRequestUrl}
              className={`transition-colors font-medium ${linkColorClass}`}>
              Login
            </SignInButton>

            <SignUpButton
              mode="modal"
              fallbackRedirectUrl={originalRequestUrl}
              className={`bg-[var(--brand-accent)] text-[var(--brand-navy)] px-5 py-2 rounded-md hover:brightness-110 transition-all font-semibold ${
                isScrolled ? 'shadow-md shadow-amber-500/20' : ''
              }`}>
              Sign Up
            </SignUpButton>
          </div>
          <div className="md:hidden">
            <button
              onClick={onMenuToggle}
              className={`focus:outline-none transition-colors text-white`}
              aria-label="Toggle menu">
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

const MobileMenu = ({ isMenuOpen, onLinkClick }) => {
  const location = useLocation();
  const originalRequestUrl = location.pathname + location.search;

  if (!isMenuOpen) return null;
  return (
    <div className="fixed inset-0 bg-[var(--brand-navy)]/95 backdrop-blur-lg z-40 md:hidden flex flex-col items-center justify-center">
      <div className="space-y-8 text-center">
        <a
          onClick={onLinkClick}
          href="#features"
          className="block text-3xl text-white hover:text-[var(--brand-accent)] transition-colors">
          Features
        </a>
        <a
          onClick={onLinkClick}
          href="#how-it-works"
          className="block text-3xl text-white hover:text-[var(--brand-accent)] transition-colors">
          How It Works
        </a>
        <a
          onClick={onLinkClick}
          href="#faq"
          className="block text-3xl text-white hover:text-[var(--brand-accent)] transition-colors">
          FAQ
        </a>
        <hr className="border-white/20 w-1/2 mx-auto" />
        <SignInButton
          mode="modal"
          fallbackRedirectUrl={originalRequestUrl}
          className="block text-2xl text-white hover:text-[var(--brand-accent)] transition-colors mx-auto">
          Login
        </SignInButton>
        <SignUpButton
          mode="modal"
          fallbackRedirectUrl={originalRequestUrl}
          className="mt-4 inline-block bg-[var(--brand-accent)] text-[var(--brand-navy)] px-8 py-4 rounded-lg font-semibold text-xl">
          Sign Up
        </SignUpButton>
      </div>
    </div>
  );
};

const Hero = () => {
  const location = useLocation();
  const originalRequestUrl = location.pathname + location.search;
  
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center text-center overflow-hidden bg-gradient-to-br from-[var(--brand-navy)] via-slate-900 to-indigo-900">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-purple-600/10 to-cyan-600/5"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      
      <Doodle key="d1" type="plane" className="doodle doodle-1 text-blue-400/30" />
      <Doodle key="d2" type="atom" className="doodle doodle-2 text-purple-400/30" />
      <Doodle key="d3" type="formula" className="doodle doodle-3 text-cyan-400/30" />
      <Doodle key="d4" type="swirl" className="doodle doodle-4 text-indigo-400/30" />
      <Doodle key="d5" type="plus" className="doodle doodle-5 text-blue-400/30" />
      <Doodle key="d6" type="atom" className="doodle doodle-6 text-purple-400/30" />
      <Doodle key="d7" type="plane" className="doodle doodle-7 text-cyan-400/30" />
      <Doodle key="d8" type="formula" className="doodle doodle-8 text-indigo-400/30" />

      <div className="relative z-10 container mx-auto px-6 flex flex-col items-center">
        <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-full text-blue-300 text-sm font-medium mb-8 backdrop-blur-sm">
          <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></span>
          4,000+ students already studying smarter
        </div>
        
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white leading-[1.1] mb-8 font-mono max-w-6xl">
          <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
            Turn Study Materials Into
          </span>
          <br />
          <span className="bg-gradient-to-r from-[var(--brand-accent)] to-yellow-300 bg-clip-text text-transparent text-glow">
            AI-Powered Learning
          </span>
        </h1>
        
        <p className="text-lg md:text-2xl text-slate-300 max-w-4xl mx-auto mb-12 leading-relaxed">
          Generate flashcards, quizzes, and summaries from any content in seconds. 
          <span className="text-blue-300 font-semibold">Stop cramming, start understanding.</span>
        </p>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center mb-12">
          <SignUpButton
            mode="modal"
            fallbackRedirectUrl={originalRequestUrl}>
            <button className="group relative inline-flex items-center justify-center px-10 py-5 text-xl font-bold text-[var(--brand-navy)] bg-gradient-to-r from-[var(--brand-accent)] to-yellow-400 rounded-2xl hover:from-yellow-400 hover:to-[var(--brand-accent)] transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-yellow-500/30">
              <span className="relative z-10">Start Learning for Free</span>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-yellow-300 to-amber-300 opacity-0 group-hover:opacity-30 transition-opacity duration-500"></div>
            </button>
          </SignUpButton>
          
          <SignInButton
            mode="modal"
            fallbackRedirectUrl={originalRequestUrl}>
            <button className="inline-flex items-center justify-center px-8 py-5 text-lg font-semibold text-slate-300 border-2 border-slate-600 rounded-2xl hover:border-blue-400 hover:text-white hover:bg-blue-600/10 transition-all duration-300 backdrop-blur-sm">
              Already have an account?
            </button>
          </SignInButton>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center max-w-4xl mx-auto">
          <div className="space-y-2">
            <div className="text-3xl font-bold text-[var(--brand-accent)]">4K+</div>
            <div className="text-sm text-slate-400">Active Students</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-blue-400">50K+</div>
            <div className="text-sm text-slate-400">AI Flashcards</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-purple-400">98%</div>
            <div className="text-sm text-slate-400">Success Rate</div>
          </div>
          <div className="space-y-2">
            <div className="text-3xl font-bold text-cyan-400">24/7</div>
            <div className="text-sm text-slate-400">AI Support</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Features = () => {
  return (
    <Section id="features" className="py-32 bg-gradient-to-br from-slate-50 via-white to-blue-50 text-[var(--text-dark)]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-24">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-6">
            <BrainCircuit className="w-4 h-4 mr-2" />
            AI-Powered Features
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent font-mono mb-6">
            Your Complete Study Toolkit
          </h2>
          <p className="text-slate-600 mt-4 text-xl max-w-3xl mx-auto leading-relaxed">
            Transform how you learn with AI tools designed to make studying faster, smarter, and more effective.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {FEATURES_DATA.map((feature, index) => (
            <div
              key={index}
              className="group relative bg-white/80 backdrop-blur-sm p-8 text-center rounded-3xl border border-slate-200/60 hover:border-blue-300/50 shadow-lg hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 transform hover:-translate-y-2">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-center h-20 w-20 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white mx-auto mb-8 transform group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/25">
                  <feature.icon size={36} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-900 transition-colors duration-300">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">{feature.description}</p>
              </div>
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

const CallToActionSection = ({ headline, showBothButtons = false }) => {
  const location = useLocation();
  const originalRequestUrl = location.pathname + location.search;
  
  return (
    <Section className="py-24 bg-gradient-to-r from-[var(--brand-navy)] via-slate-900 to-[var(--brand-navy)] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10"></div>
      <div className="container mx-auto px-6 text-center relative z-10">
        <h2 className="text-3xl md:text-5xl font-bold text-white font-mono mb-8 leading-tight">{headline}</h2>
        <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">Join thousands of students who are already studying smarter with AI</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <SignUpButton
            mode="modal"
            fallbackRedirectUrl={originalRequestUrl}>
            <button className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-[var(--brand-navy)] bg-gradient-to-r from-[var(--brand-accent)] to-yellow-400 rounded-full hover:from-yellow-400 hover:to-[var(--brand-accent)] transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-yellow-500/25">
              <span className="relative z-10">Start Learning for Free</span>
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-300 to-amber-300 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
            </button>
          </SignUpButton>
          {showBothButtons && (
            <SignInButton
              mode="modal"
              fallbackRedirectUrl={originalRequestUrl}>
              <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-slate-600 rounded-full hover:border-[var(--brand-accent)] hover:text-[var(--brand-accent)] transition-all duration-300">
                Already have an account?
              </button>
            </SignInButton>
          )}
        </div>
      </div>
    </Section>
  );
};

const HowItWorks = () => {
  return (
    <Section id="how-it-works" className="py-32 bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 text-[var(--text-dark)]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-24">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 rounded-full text-sm font-semibold mb-6">
            <Target className="w-4 h-4 mr-2" />
            Simple Process
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent font-mono mb-6">
            Get Started in 3 Simple Steps
          </h2>
          <p className="text-slate-600 text-xl max-w-3xl mx-auto leading-relaxed">
            Transform your study materials into powerful learning tools in minutes, not hours.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16 text-center relative">
          {/* Connection lines for desktop */}
          <div className="hidden md:block absolute top-24 left-1/3 w-1/3 h-0.5 bg-gradient-to-r from-blue-400 to-indigo-500 transform translate-x-12"></div>
          <div className="hidden md:block absolute top-24 right-1/3 w-1/3 h-0.5 bg-gradient-to-r from-indigo-500 to-purple-600 transform -translate-x-12"></div>
          
          {HOW_IT_WORKS_STEPS.map((step, index) => (
            <div key={index} className="relative group" style={{ transitionDelay: `${index * 150}ms` }}>
              <div className="relative bg-white/80 backdrop-blur-sm p-8 rounded-3xl border border-slate-200/60 shadow-lg hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 transform hover:-translate-y-2">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center h-24 w-24 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white mx-auto mb-8 shadow-2xl shadow-blue-500/25 transform group-hover:scale-110 transition-transform duration-300">
                    <step.icon size={44} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-900 transition-colors duration-300">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed group-hover:text-slate-700 transition-colors duration-300">{step.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

const ProductDemo = () => {
  return (
    <Section id="demo" className="py-32 bg-gradient-to-br from-slate-50 via-white to-purple-50 text-[var(--text-dark)]">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold mb-6">
            <BookOpen className="w-4 h-4 mr-2" />
            Live Demo
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-slate-900 via-purple-900 to-indigo-900 bg-clip-text text-transparent font-mono mb-6">See ZenshKool in Action</h2>
          <p className="text-slate-600 mt-4 text-xl max-w-3xl mx-auto leading-relaxed">
            Watch how students transform their study materials into AI-powered learning tools in real-time.
          </p>
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-3xl opacity-20 group-hover:opacity-30 transition-opacity duration-500"></div>
            <div className="relative aspect-video bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl shadow-2xl overflow-hidden border border-slate-200/20">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/LXb3EKWsInQ?si=abcdef123456"
                title="ZenshKool Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="rounded-3xl"></iframe>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Testimonials = () => {
  const extendedTestimonials = [...TESTIMONIALS_DATA, ...TESTIMONIALS_DATA];

  return (
    <Section id="testimonials" className="py-32 bg-gradient-to-br from-[var(--brand-navy)] via-slate-900 to-indigo-900 text-[var(--text-light)] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
      <div className="container mx-auto relative z-10">
        <div className="text-center mb-24">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-500/30 text-yellow-300 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
            <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2 animate-pulse"></span>
            Student Success Stories
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent font-mono mb-6">
            Loved by Learners Everywhere
          </h2>
          <p className="text-slate-300 text-xl max-w-3xl mx-auto leading-relaxed">
            Join thousands of students who've transformed their study habits with AI
          </p>
        </div>
        <div className="scrolling-testimonials-container relative w-full overflow-hidden">
          <div className="scrolling-testimonials flex gap-8">
            {extendedTestimonials.map((testimonial, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-80 md:w-96 bg-white/10 backdrop-blur-lg p-8 rounded-3xl border border-white/20 hover:border-blue-400/50 hover:bg-white/15 transition-all duration-300">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-lg">★</span>
                  ))}
                </div>
                <p className="text-lg text-slate-200 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-14 h-14 rounded-full mr-4 flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold text-xl shadow-lg">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg">{testimonial.name}</h4>
                    <p className="text-sm font-medium text-blue-300">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => setOpenIndex(openIndex === index ? null : index);

  return (
    <Section id="faq" className="py-32 bg-gradient-to-br from-slate-50 via-white to-blue-50 text-[var(--text-dark)]">
      <div className="container mx-auto px-6 max-w-5xl">
        <div className="text-center mb-24">
          <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-6">
            <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
            Got Questions?
          </div>
          <h2 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 bg-clip-text text-transparent font-mono mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-slate-600 text-xl max-w-3xl mx-auto leading-relaxed">
            Everything you need to know about getting started with ZenshKool
          </p>
        </div>
        <div className="space-y-6">
          {FAQ_DATA.map((faq, index) => (
            <div key={index} className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300">
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex justify-between items-center text-left p-8 hover:bg-blue-50/50 transition-colors duration-300 rounded-2xl">
                <span className="text-xl font-bold text-slate-900 pr-4">{faq.question}</span>
                <ChevronDown
                  className={`flex-shrink-0 w-6 h-6 transform transition-all duration-300 ${
                    openIndex === index ? 'rotate-180 text-blue-600' : 'text-slate-400'
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openIndex === index ? 'max-h-96 pb-8' : 'max-h-0'
                }`}>
                <div className="px-8">
                  <div className="w-full h-px bg-gradient-to-r from-blue-200 to-indigo-200 mb-6"></div>
                  <p className="text-slate-600 leading-relaxed text-lg">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-t border-slate-700/50">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-slate-300">
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <img src={brogrammers_log} alt="logo" className="size-10 rounded-xl" />
              <h3 className="text-3xl font-bold text-white font-mono">ZenshKool</h3>
            </div>
            <p className="text-slate-400 max-w-sm leading-relaxed">
              AI-powered learning tools designed to help students achieve academic excellence through smarter study methods.
            </p>
            <div className="flex space-x-6">
              <a
                href="#"
                className="text-slate-400 hover:text-[var(--brand-accent)] transition-colors transform hover:scale-110 duration-200"
                aria-label="Instagram">
                <Instagram size={24} />
              </a>
              <a
                href="#"
                className="text-slate-400 hover:text-[var(--brand-accent)] transition-colors transform hover:scale-110 duration-200"
                aria-label="Twitter">
                <Twitter size={24} />
              </a>
              <a
                href="#"
                className="text-slate-400 hover:text-[var(--brand-accent)] transition-colors transform hover:scale-110 duration-200"
                aria-label="LinkedIn">
                <Linkedin size={24} />
              </a>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h4 className="font-bold text-white mb-6 text-lg">Product</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#features" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#how-it-works" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    How it Works
                  </a>
                </li>
                <li>
                  <a href="#testimonials" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    Testimonials
                  </a>
                </li>
                <li>
                  <a href="#faq" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-6 text-lg">Company</h4>
              <ul className="space-y-3">
                <li>
                  <a href="#" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="/privacy" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="hover:text-[var(--brand-accent)] transition-colors duration-200">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-700/50 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-slate-500">&copy; {new Date().getFullYear()} ZenshKool. All rights reserved.</p>
            <p className="text-slate-500 text-sm">
              Built with ❤️ for students worldwide
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    if (window.scrollY > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <button
      onClick={scrollToTop}
      className={`fixed bottom-8 right-8 bg-[var(--brand-accent)] text-[var(--brand-navy)] p-3 rounded-full shadow-lg hover:brightness-110 transition-all duration-300 transform ${
        isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'
      }`}
      aria-label="Scroll to top">
      <ArrowUp size={24} />
    </button>
  );
};

// --- Main App Component ---

export default function App() {
  const [isMenuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
  }, [isMenuOpen]);

  const pageStyles = `
        :root {
            --brand-navy: #0A192F;
            --brand-accent: #FFD700; 
            --brand-accent-dark: #CCA700; 
            --bg-dark: #0A192F; 
            --bg-light: #F0F8FF; 
            --text-dark: #112240;
            --text-light: #E6F1FF; 
            --text-slate: #8892b0;
        }
        html { scroll-behavior: smooth; }
        body { font-family: 'Inter', sans-serif; background-color: var(--bg-dark); color: var(--text-light); }
        .font-mono { font-family: 'Space Mono', 'Courier New', monospace; }
        .bg-grid-pattern { background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px); background-size: 50px 50px; }
        .text-glow { text-shadow: 0 0 8px rgba(255, 215, 0, 0.3), 0 0 12px rgba(255, 215, 0, 0.2); }
        
        .glowing-btn {
            background: linear-gradient(135deg, var(--brand-accent), #ffed4e);
            color: var(--brand-navy);
            box-shadow: 0 8px 32px rgba(255, 215, 0, 0.3), 0 4px 16px rgba(255, 215, 0, 0.2);
            transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            position: relative;
            overflow: hidden;
        }
        .glowing-btn:hover {
            box-shadow: 0 12px 40px rgba(255, 215, 0, 0.4), 0 6px 20px rgba(255, 215, 0, 0.3);
            transform: translateY(-2px);
            filter: brightness(1.1);
        }
        .glowing-btn::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
            transition: left 0.6s;
        }
        .glowing-btn:hover::before {
            left: 100%;
        }

        .doodle {
            position: absolute; opacity: 0.2; animation: float 20s ease-in-out infinite;
        }
        .doodle-1 { top: 15%; left: 10%; width: 60px; height: 60px; animation-delay: 0s; }
        .doodle-2 { top: 20%; right: 15%; width: 80px; height: 80px; animation-delay: 5s; }
        .doodle-3 { bottom: 10%; left: 20%; width: 100px; height: 40px; animation-delay: 10s; }
        .doodle-4 { bottom: 25%; right: 10%; width: 50px; height: 50px; animation-delay: 2s; }
        .doodle-5 { top: 50%; left: 5%; width: 40px; height: 40px; animation-delay: 7s; }
        .doodle-6 { top: 75%; right: 25%; width: 70px; height: 70px; animation-delay: 12s; }
        .doodle-7 { bottom: 5%; right: 40%; width: 60px; height: 60px; animation-delay: 15s; }
        .doodle-8 { top: 5%; left: 45%; width: 90px; height: 36px; animation-delay: 18s; }
        
        @keyframes float {
            0% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-40px) rotate(20deg); }
            100% { transform: translateY(0px) rotate(0deg); }
        }

        /* --- Testimonial Marquee --- */
        .scrolling-testimonials-container {
            -webkit-mask-image: linear-gradient(to right, transparent, white 15%, white 85%, transparent);
            mask-image: linear-gradient(to right, transparent, white 15%, white 85%, transparent);
        }
        .scrolling-testimonials {
            animation: scroll 80s linear infinite;
        }
        .scrolling-testimonials-container:hover .scrolling-testimonials {
            animation-play-state: paused;
        }
        @keyframes scroll {
            from { transform: translateX(0); }
            to { transform: translateX(-50%); }
        }
        
        /* --- Enhanced animations --- */
        @keyframes fadeInUp {
            from {
                opacity: 0;
                transform: translateY(30px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }
        
        @keyframes shimmer {
            0% {
                background-position: -200% center;
            }
            100% {
                background-position: 200% center;
            }
        }
    `;

  return (
    <>
      <style>{pageStyles}</style>
      <div className="bg-[var(--bg-dark)]">
        <Header onMenuToggle={() => setMenuOpen(!isMenuOpen)} isMenuOpen={isMenuOpen} />
        <MobileMenu isMenuOpen={isMenuOpen} onLinkClick={() => setMenuOpen(false)} />
        <main>
          <Hero />
          <Features />
          <CallToActionSection headline="Ready to Study Smarter?" />
          <HowItWorks />
          <ProductDemo />
          <CallToActionSection headline="Stop Cramming. Start Understanding." showBothButtons={true} />
          <Testimonials />
          <FAQ />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}
